import React, { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionButton } from '../ActionButton';
import { IconChevronLeft, IconChevronRight } from '../Icon';
import { Image, Item, List, ListWrapper, NextBtn, PrevBtn, Wrapper } from './wrapper';

export interface ICarouselItem {
  src: string;
  srcSet?: string;
  alt: string;
}

export interface ICarousel {
  items: ICarouselItem[];
  itemKeyBase?: string;
  onChangeImage?: () => void;
}

const Carousel = ({ items, itemKeyBase, onChangeImage }: ICarousel) => {
  const { t } = useTranslation(['other']);
  const [scrollSnap, setScrollSnap] = useState(true);
  const overflowElement = useRef<HTMLUListElement | null>(null);

  const onPrev = useCallback(() => {
    const itemWidth = overflowElement.current?.children[0].clientWidth || 0;
    const scrollLeft = overflowElement.current?.scrollLeft || 0;

    if (scrollLeft > 0) {
      setScrollSnap(false);
      setTimeout(() => {
        setScrollSnap(true);
      }, 500);

      if (overflowElement?.current) {
        overflowElement.current.scroll({
          left: scrollLeft - itemWidth,
          behavior: 'smooth',
        });
        if (onChangeImage) {
          onChangeImage();
        }
      }
    }
  }, []);

  const onNext = useCallback(() => {
    const itemWidth = overflowElement.current?.children[0].clientWidth || 0;
    const scrollWidth = overflowElement.current?.scrollWidth || 0;
    const scrollLeft = overflowElement.current?.scrollLeft || 0;
    const overflowElementWidth = overflowElement.current?.offsetWidth || 0;
    const overflowElementScrollWidth = scrollWidth - overflowElementWidth;

    if (overflowElementScrollWidth !== scrollLeft) {
      setScrollSnap(false);
      setTimeout(() => {
        setScrollSnap(true);
      }, 500);

      if (overflowElement?.current) {
        overflowElement.current.scroll({
          left: scrollLeft + itemWidth,
          behavior: 'smooth',
        });
        if (onChangeImage) {
          onChangeImage();
        }
      }
    }
  }, []);

  return (
    <Wrapper>
      <ListWrapper>
        <List ref={overflowElement} $scrollSnap={scrollSnap}>
          {items.map((item, i) => (
            <Item key={itemKeyBase ? `${itemKeyBase}-${i}` : `overflow-grid-item-${i}`}>
              <Image src={item.src} srcSet={item.srcSet} alt={item.alt} />
            </Item>
          ))}
        </List>
      </ListWrapper>
      {items.length > 1 && (
        <>
          <PrevBtn>
            <ActionButton onClick={onPrev} title={t('previousImage', { ns: 'other' })}>
              <IconChevronLeft />
            </ActionButton>
          </PrevBtn>
          <NextBtn>
            <ActionButton onClick={onNext} title={t('nextImage', { ns: 'other' })}>
              <IconChevronRight />
            </ActionButton>
          </NextBtn>
        </>
      )}
    </Wrapper>
  );
};

export default Carousel;
